import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, ExternalLink, Link as LinkIcon, PawPrint, ClipboardList } from "lucide-react";
import { Link, useLocation } from "wouter";
import { format } from "date-fns";
import { FollowAnimalButton } from "@/components/follow-animal-button";

interface AnimalActionMenuProps {
  animalId: string;
  animalName?: string;
  onViewPreview?: (animalId: string) => void;
  animalSpecies?: string;
  animalPhotoUrl?: string | null;
  onAddTo?: (animalId: string, animalName?: string, animalSpecies?: string, animalPhotoUrl?: string | null) => void;
  triggerVariant?: "ghost" | "outline";
  align?: "start" | "end";
  navigationOnly?: boolean;
}

export function AnimalActionMenu({
  animalId,
  animalName,
  onViewPreview,
  animalSpecies,
  animalPhotoUrl,
  onAddTo,
  triggerVariant = "ghost",
  align = "end",
  navigationOnly = false,
}: AnimalActionMenuProps) {
  const [, setLocation] = useLocation();

  const displayLabel = animalName ? `${animalName} (${animalId})` : animalId;

  if (navigationOnly) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={triggerVariant}
            size="icon"
            data-testid={`button-animal-actions-${animalId}`}
          >
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Actions for {displayLabel}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-52">
          <DropdownMenuItem
            onClick={() => {
              const today = format(new Date(), "yyyy-MM-dd");
              setLocation(`/interactions?animalId=${encodeURIComponent(animalId)}&startDate=2020-01-01&endDate=${today}`);
            }}
            data-testid={`button-view-interactions-${animalId}`}
          >
            <ClipboardList className="h-4 w-4 mr-2" />
            View Interactions
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href={`/animals/${encodeURIComponent(animalId)}`}
              data-testid={`link-view-animal-full-${animalId}`}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Full Profile
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={triggerVariant}
          size="icon"
          data-testid={`button-animal-actions-${animalId}`}
        >
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Actions for {displayLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-52">
        <DropdownMenuLabel>Interactions</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => {
            const today = format(new Date(), "yyyy-MM-dd");
            setLocation(`/interactions?animalId=${encodeURIComponent(animalId)}&startDate=2020-01-01&endDate=${today}`);
          }}
          data-testid={`button-view-interactions-${animalId}`}
        >
          <ClipboardList className="h-4 w-4 mr-2" />
          View Interactions
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Animal Profile</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => onViewPreview?.(animalId)}
          data-testid={`button-view-animal-preview-${animalId}`}
        >
          <PawPrint className="h-4 w-4 mr-2" />
          View Preview
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            href={`/animals/${encodeURIComponent(animalId)}`}
            data-testid={`link-view-animal-full-${animalId}`}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View Full Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Tasks / Meetings</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => onAddTo?.(animalId, animalName, animalSpecies, animalPhotoUrl)}
          data-testid={`button-add-to-${animalId}`}
        >
          <LinkIcon className="h-4 w-4 mr-2" />
          Add to...
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <FollowAnimalButton
          animalId={animalId}
          animalName={animalName}
          variant="menu-item"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
